'use client';

import { useEffect, useRef } from 'react';
import { CURSOR } from '@/lib/motionTokens';

// A small accent ring that follows the pointer, growing when it crosses an
// interactive element. Lives on top of the SmokeyCursorEffect — the smokey
// trail provides atmosphere, this dot provides state. Sits on its own
// transform layer so updates never trigger layout.
//
// Disabled on coarse pointers (touch) and when the user prefers reduced
// motion. We don't hide the native cursor on those — only when the custom
// one is actually drawing.

const HOVER_SELECTOR =
  'a, button, [role="button"], [data-cursor="hover"], iframe, label';

const { followLerp: FOLLOW_LERP } = CURSOR;

export function CustomCursor() {
  const ringRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (coarse || reduced) return;

    const ring = ringRef.current;
    if (!ring) return;

    // Activate the dot only after we're mounted on a fine pointer device —
    // the CSS keeps it hidden by default so SSR never flashes a misplaced
    // ring before the JS runs.
    document.documentElement.classList.add('cursor-custom-active');
    ring.style.opacity = '1';

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let tx = x;
    let ty = y;
    let raf = 0;
    let lastHoverEl: Element | null = null;
    let lastT = performance.now();

    const tick = () => {
      // Time-normalized lerp: followLerp is calibrated for a 60fps frame,
      // so scale the closing fraction by elapsed time. When the frame rate
      // dips (both canvases render during the EndPortal orbit window) the
      // ring keeps the same real-time responsiveness instead of feeling
      // heavier the lower the fps goes.
      const now = performance.now();
      const dt = Math.min(100, now - lastT);
      lastT = now;
      const alpha = 1 - Math.pow(1 - FOLLOW_LERP, dt / (1000 / 60));
      x += (tx - x) * alpha;
      y += (ty - y) * alpha;
      ring.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    const onMove = (e: PointerEvent) => {
      tx = e.clientX;
      ty = e.clientY;
    };

    // mouseover bubbles, so a single listener covers the whole document.
    // Toggle the class only when the closest hover ancestor actually changes
    // to avoid touching the DOM on every micro-move.
    const onOver = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const hit = target?.closest?.(HOVER_SELECTOR) ?? null;
      if (hit !== lastHoverEl) {
        lastHoverEl = hit;
        ring.classList.toggle('cursor-ring--hover', !!hit);
      }
    };

    const onLeave = () => {
      ring.style.opacity = '0';
    };
    const onEnter = () => {
      ring.style.opacity = '1';
    };

    // Click state — contracts the ring on pointerdown for tactile feedback,
    // releases on pointerup/cancel. Animates via the existing width/height
    // transition so it stays inside the cursor's motion language.
    const onDown = () => ring.classList.add('cursor-ring--pressed');
    const onUp = () => ring.classList.remove('cursor-ring--pressed');

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    document.addEventListener('mouseover', onOver, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    document.documentElement.addEventListener('mouseenter', onEnter);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.removeEventListener('mouseover', onOver);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      document.documentElement.removeEventListener('mouseenter', onEnter);
      document.documentElement.classList.remove('cursor-custom-active');
    };
  }, []);

  return <div ref={ringRef} className="cursor-ring" aria-hidden="true" />;
}
